import React, {createContext, useRef, useState} from "react";

import {Button, Card, Col, Divider, InputNumber, Layout, Modal, Row, Spin, Tabs, Tag} from 'antd';
import CheckUpTable from "./Table";
import {
    ALLOWED_LOWER_BOUND,
    DEFAULT_CONFIGURATIONS,
    DEFAULT_MODEL,
    mapColumns,
    OUTLIER_LOWER_BOUND
} from "./Util"
import PdbForm from "./Form";
import {FormOutlined, LoadingOutlined} from '@ant-design/icons';
import MolecularVisualizer from "./MolecularVisualizer";
import RamachandranAnalysis from "./RamachandranAnalysis";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGreaterThanEqual, faLessThan} from "@fortawesome/free-solid-svg-icons";
import {ALLOWED_COLOR_LIGHT, FAVORED_COLOR_LIGHT, OUTLIER_COLOR_LIGHT} from "./ColorUtil";
import Navbar from "./Navbar/NavBar";

const antIcon = <div><LoadingOutlined style={{ fontSize: 60 }} spin /><span>Loading...</span></div>;

export const ConfigContext = createContext(null);

const isValidThreshold = (outlierThreshold, allowedThreshold) => {
    if (outlierThreshold >= allowedThreshold) {
        Modal.error({
            title: 'Incorrect Threshold',
            content: 'Outlier Threshold has to be less than the Allowed Threshold',
        });
        return false;
    }

    return true;
}


function ThresholdHandler({setOutlierLowerBound, setAllowedLowerBound}) {
    const outlierLBInputElement = useRef();
    const allowedLBInputElement = useRef();
    return (
        <div style={{paddingBottom:'0.5em', paddingRight: '1vw', paddingLeft: '1vw', borderWidth:2, borderRadius:"8px", border:"1px solid #ededed"}}>
            <h4>3-way membership thresholds for validation</h4>
            <Tag color={OUTLIER_COLOR_LIGHT} style={{fontSize: "1.0em", color: "black"}}>Outlier</Tag>
            <FontAwesomeIcon icon={faLessThan} style={{marginRight: 12}}/>
            <InputNumber ref={outlierLBInputElement} defaultValue={OUTLIER_LOWER_BOUND} size="medium" maxLength={5} controls={false}
                         style={{maxWidth: 50}} decimalSeparator={"."}/>
            <span style={{color:"#494646", fontSize:"0.9em", marginLeft:'0.5em'}}>bits</span>
            <Divider style={{
                borderRadius: 90,
                fontSize: 25,
                fontWeight: 900,
                borderWidth: 'medium',
                backgroundColor: "#000000"
            }} type="vertical"/>
            <Tag color={ALLOWED_COLOR_LIGHT} style={{fontSize: "1.0em", color: "black"}}>Allowed</Tag>
            <FontAwesomeIcon icon={faLessThan} style={{marginRight: 12}}/>
            <InputNumber ref={allowedLBInputElement} defaultValue={ALLOWED_LOWER_BOUND} size="medium" maxLength={5} controls={false}
                         style={{maxWidth: 50}} decimalSeparator={"."}/>
            <span style={{color:"#494646", fontSize:"0.9em", marginLeft:'0.5em'}}>bits</span>
            <Divider style={{
                borderRadius: 90,
                fontSize: 25,
                fontWeight: 900,
                borderWidth: 'medium',
                backgroundColor: "#000000"
            }} type="vertical"/>
            <Tag color={FAVORED_COLOR_LIGHT} style={{fontSize: "1.0em", color: "black"}}>Favoured</Tag>
            <FontAwesomeIcon icon={faGreaterThanEqual} style={{marginRight: 12, marginLeft: 12}}/>
            {allowedLBInputElement.current ? allowedLBInputElement.current.value: ALLOWED_LOWER_BOUND}
            <span style={{color:"#494646", fontSize:"0.9em", marginLeft:'0.5em'}}>bits</span>
            <Button style={{marginLeft: 12}} type="primary" onClick={() => {
                const outlierVal = parseFloat(outlierLBInputElement.current?.value)
                const allowedVal = parseFloat(allowedLBInputElement.current?.value)
                if (isValidThreshold( outlierVal, allowedVal)) {
                    setOutlierLowerBound(outlierVal);
                    setAllowedLowerBound(allowedVal);
                }

            }}>Apply</Button>
        </div>
    )
}

function App() {

    const [pdbData, setPdbData] = useState([])
    const [model, setModel] = useState(DEFAULT_MODEL)
    const [dynamicColumns, setDynamicColumns] = useState(mapColumns(DEFAULT_CONFIGURATIONS))
    const [fileName, setFileName] = useState("")
    const [pymolUrl, setPymolUrl] = useState("")
    const [isLoading, setLoadingState] = useState(false)
    const [uploadedFile, setUploadedFile] = useState(null);
    const [outlierLowerBound, setOutlierLowerBound] = useState(OUTLIER_LOWER_BOUND)
    const [allowedLowerBound, setAllowedLowerBound] = useState(ALLOWED_LOWER_BOUND)

    const ThresholdHandlerComponent = ThresholdHandler({setOutlierLowerBound, setAllowedLowerBound})

    const tabs = [
        {
            name: "PhiSiCal-Checkup Report",
            component: <CheckUpTable
                data={pdbData}
                fileName={fileName}
                pymolUrl={pymolUrl}
                dynamicColumns={dynamicColumns}
            />
        },
        {
            name: "Structure",
            component: <MolecularVisualizer pdb_id={fileName} data={pdbData} dynamicColumns={dynamicColumns} uploadedFile={uploadedFile}/>
        },
        {
            name: "Contour Analysis",
            component: <RamachandranAnalysis data={pdbData} model = {model} pdb_id={fileName} dynamicColumns={dynamicColumns}/>
        }
    ]

    return (
        <ConfigContext.Provider value={{outlierLowerBound, allowedLowerBound, setOutlierLowerBound, setAllowedLowerBound }}>
                <Spin indicator={antIcon} spinning={isLoading} style={{ top: '40%' }} >
                    <Layout style={{ backgroundColor:'#FFFFFF', minHeight:"100vh", }}>
                        <div style={{position: 'sticky', top: 0, zIndex: 10000}}>
                            <Navbar/>
                        </div>
                        <Layout.Content style={{display:"flex", flexDirection:"column", backgroundColor:'#FFFFFF' , gap:40}}>
                            <Row>
                                <Col span={24} align="middle"></Col>
                            </Row>
                            <Row >
                                <Col xs={1} sm={4} md={5} lg={5} xl={7} xxl={8} align="end" />
                                <Col xs={22} sm={16} md={14} lg={14} xl={10} xxl={8}>
                                    <Card title={<span><FormOutlined  style={{paddingRight:5}}/> PhiSiCal (𝜙𝛹𝜒al) Checkup Wizard </span>} >
                                        <PdbForm
                                            updateData={setPdbData}
                                            setFileName={setFileName}
                                            setPymolUrl={setPymolUrl}
                                            setLoadingState={setLoadingState}
                                            setDynamicColumns={setDynamicColumns}
                                            setUploadedFile={setUploadedFile}
                                            setModel = {setModel}
                                        />
                                    </Card>

                                </Col>
                                <Col  xs={1}  sm={4} md={5} lg={5} xl={7} xxl={8} align="end" />
                            </Row>
                            <Row >
                                <Col span={24} align="middle" style={{overflowX:"auto", scrollbarWidth:'thin', paddingLeft:10, paddingRight:10}}>
                                    {!isLoading && <Tabs
                                        type="card"
                                        tabBarExtraContent={ThresholdHandlerComponent}
                                        items={tabs.map(tab => {
                                            return {
                                                label: tab.name,
                                                key: tab.name,
                                                children: tab.component
                                            };
                                        })}
                                    />
                                    }
                                </Col>
                            </Row>

                        </Layout.Content>
                        <Layout.Footer style={{width: '100%', textAlign: 'right', zIndex:1000, backgroundColor:"#58B2DC"}}><b>About
                            <a href="https://lcb.infotech.monash.edu/phisical/" target="_blank" rel="noreferrer" style={{color:"#1402f6"}}> 𝜙𝛹𝜒al</a> |
                            <a href="https://lcb.infotech.monash.edu/" target="_blank" rel="noreferrer" style={{color:"#1402f6"}}> LCB Home</a> |v1.0.0 </b>
                        </Layout.Footer>
                    </Layout>
                </Spin>

        </ConfigContext.Provider>
    );
}

export default App;